import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Check } from "react-feather";
import { EFFORT_LABEL, OFFERED_EFFORTS, type OfferedEffort } from "../lib/effortLevel";
import "../styles/ops-build-polish.css";

/* ------------------------------------------------------------------------
   Thinking level (ported from Build, 9 Oct 2026): the chip opens this menu
   instead of cycling blindly. An anchored popover with a mouse on a wide
   screen, a bottom sheet with Cancel on a phone / touch. Just the level names
   and a check on the selected one (Julian: no dots, no descriptions, no note).
   Styles: styles/ops-build-polish.css (self-contained .thinkMenu rules).
   ------------------------------------------------------------------------ */
const THINK_NAMES: Record<OfferedEffort, string> = {
  medium: EFFORT_LABEL.medium, high: EFFORT_LABEL.high, xhigh: EFFORT_LABEL.xhigh, max: EFFORT_LABEL.max,
};

function thinkPopoverMode(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    && window.matchMedia("(min-width: 760px) and (hover: hover)").matches;
}

export default function ThinkingLevelMenu({ current, anchor, onPick, onClose }: {
  current: string;
  anchor: HTMLElement | null;
  onPick: (level: OfferedEffort) => void;
  onClose: () => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const popover = thinkPopoverMode();

  useEffect(() => {
    const root = rootRef.current;
    const frame = requestAnimationFrame(() => {
      (root?.querySelector<HTMLElement>(".thinkMenu__item.is-on") ?? root?.querySelector<HTMLElement>(".thinkMenu__item"))?.focus({ preventScroll: true });
    });
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); return; }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Tab" && e.key !== "Home" && e.key !== "End") return;
      // Arrows move between levels; Tab also reaches Cancel (focus stays inside).
      const items = Array.from(root?.querySelectorAll<HTMLElement>(e.key === "Tab" ? ".thinkMenu__item, .thinkMenu__cancel" : ".thinkMenu__item") ?? [])
        .filter((el) => el.getClientRects().length > 0);
      if (!items.length) return;
      e.preventDefault();
      if (e.key === "Home") { items[0]?.focus({ preventScroll: true }); return; }
      if (e.key === "End") { items[items.length - 1]?.focus({ preventScroll: true }); return; }
      const index = items.indexOf(document.activeElement as HTMLElement);
      const down = e.key === "ArrowDown" || (e.key === "Tab" && !e.shiftKey);
      items[(index + (down ? 1 : -1) + items.length) % items.length]?.focus({ preventScroll: true });
    }
    document.addEventListener("keydown", onKey, true);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKey, true);
      if (anchor?.isConnected) anchor.focus({ preventScroll: true });
    };
  }, [anchor, onClose]);

  let style: React.CSSProperties | undefined;
  if (popover && anchor) {
    const r = anchor.getBoundingClientRect(), width = 220;
    style = { width, left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)), bottom: Math.max(8, window.innerHeight - r.top + 8) };
  }
  return createPortal(
    <div ref={rootRef} className={`thinkMenu ${popover ? "is-popover" : "is-sheet"}`} onClick={onClose}
      role="dialog" aria-modal="true" aria-labelledby="think-menu-title">
      <div className="thinkMenu__panel" style={style} onClick={(e) => e.stopPropagation()}>
        <div className="thinkMenu__grabber" aria-hidden="true" />
        <div className="thinkMenu__title" id="think-menu-title">Thinking level</div>
        <div className="thinkMenu__items" role="menu" aria-label="Thinking level">
          {OFFERED_EFFORTS.map((key) => {
            const on = key === current;
            return (
              <button key={key} type="button" role="menuitemradio" aria-checked={on}
                className={`thinkMenu__item ${on ? "is-on" : ""}`}
                // Always picks (the store ignores an unchanged level), so Max on a legacy
                // stored "ultra" saves a real "max".
                onClick={() => { onPick(key); onClose(); }}>
                <span className="thinkMenu__name">{THINK_NAMES[key]}</span>
                {on && <Check className="thinkMenu__check" width={18} height={18} aria-hidden="true" />}
              </button>
            );
          })}
        </div>
        <button type="button" className="thinkMenu__cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>,
    document.body,
  );
}
